import React from 'react';

const Donate = () => (
  <div className="bg-ehh-cream text-ehh-navy">
    {/* Hero Section */}
    <section className="relative min-h-[80vh] flex items-center justify-center text-center overflow-hidden">
      <img 
        src="/assets/images/donate-hero-raised-hands.png" 
        alt="Raised hands receiving hope and light in Zambian community" 
        className="absolute inset-0 w-full h-full object-cover" 
      />
      <div className="absolute inset-0 bg-ehh-navy/75"></div>
      <div className="relative z-10 container mx-auto px-6 sm:px-8 md:px-12 max-w-5xl">
        <div className="py-12 sm:py-16 md:py-20">
          <img src="/logo.png" alt="EMA House of Hope Logo" className="mx-auto h-24 sm:h-28 md:h-32 mb-6 sm:mb-8" />
          <h1 className="text-3xl sm:text-4xl md:text-6xl font-bold mb-4 sm:mb-6 text-ehh-cream">
            Partner in Transformation
          </h1>
          <p className="text-lg sm:text-xl md:text-2xl max-w-4xl mx-auto mb-8 sm:mb-10 leading-relaxed text-ehh-peach px-4 sm:px-0">
            Your generous contribution empowers EMA House of Hope to synchronize interventions across foundational well-being, economic empowerment, and community health—driving sustainable, ecosystem-level change in Zambia.
          </p>
          <a 
            href="#giving-options" 
            className="inline-block bg-ehh-orange text-ehh-cream px-8 py-4 sm:px-12 sm:py-5 rounded-md text-lg sm:text-xl font-semibold hover:bg-ehh-coral transition-shadow shadow-xl"
          >
            Contribute Today
          </a>
        </div>
      </div>
    </section>

    {/* Impact Section - Qualitative only, no specific amounts */}
    <section className="py-24 px-6 bg-white">
      <div className="container mx-auto max-w-6xl">
        <h2 className="text-3xl md:text-5xl font-bold text-center mb-16">Tangible Impact of Your Support</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-12">
          <div className="bg-ehh-cream rounded-2xl shadow-xl overflow-hidden flex flex-col">
            <img 
              src="/assets/images/social-family.png" 
              alt="Happy Zambian family with nutrition support" 
              className="w-full h-72 object-cover" 
            />
            <div className="p-10 flex-grow flex flex-col justify-between">
              <div>
                <h3 className="text-2xl font-semibold text-ehh-orange mb-4">Social Protection</h3>
                <p className="text-base mb-6 leading-relaxed">Delivering essential meals and psycho-social counselling to stabilise vulnerable households and restore dignity.</p>
              </div>
              <div className="bg-ehh-orange/10 rounded-xl p-6 text-center">
                <p className="text-base mt-2">Your support provides critical nutrition and psycho-social care to vulnerable families, helping break the cycle of hunger and instability.</p>
              </div>
            </div>
          </div>

          <div className="bg-ehh-cream rounded-2xl shadow-xl overflow-hidden flex flex-col">
            <img 
              src="/assets/images/health-mother-child.jpg" 
              alt="Mother and child receiving health care" 
              className="w-full h-72 object-cover" 
            />
            <div className="p-10 flex-grow flex flex-col justify-between">
              <div>
                <h3 className="text-2xl font-semibold text-ehh-orange mb-4">Community Health</h3>
                <p className="text-base mb-6 leading-relaxed">Deploying mobile clinics and advocacy to fortify local health systems for lasting resilience.</p>
              </div>
              <div className="bg-ehh-orange/10 rounded-xl p-6 text-center">
                <p className="text-base mt-2">Your support provides essential health services and care to vulnerable community members, building generational well-being.</p>
              </div>
            </div>
          </div>

          <div className="bg-ehh-cream rounded-2xl shadow-xl overflow-hidden flex flex-col">
            <img 
              src="/assets/images/economic-woman-training.jpg" 
              alt="Zambian woman in skills training" 
              className="w-full h-72 object-cover" 
            />
            <div className="p-10 flex-grow flex flex-col justify-between">
              <div>
                <h3 className="text-2xl font-semibold text-ehh-orange mb-4">Economic Resilience</h3>
                <p className="text-base mb-6 leading-relaxed">Offering market-driven vocational training to cultivate self-reliance and entrepreneurship.</p>
              </div>
              <div className="bg-ehh-orange/10 rounded-xl p-6 text-center">
                <p className="text-base mt-2">Your support equips emerging entrepreneurs with training and resources, fostering sustainable livelihoods and economic independence.</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>

    {/* Giving Options Section */}
    <section id="giving-options" className="py-24 px-6 bg-ehh-navy/5">
      <div className="container mx-auto max-w-6xl">
        <h2 className="text-3xl md:text-5xl font-bold text-center mb-16">Secure Ways to Contribute</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-12">
          <div className="bg-white rounded-2xl shadow-xl p-12 text-center">
            <h3 className="text-2xl font-semibold text-ehh-orange mb-6">International Online</h3>
            <p className="text-base mb-10 leading-relaxed">Secure credit/debit card donations via our trusted international platform.</p>
            <a 
              href="https://www.gofundme.com" 
              target="_blank" 
              rel="noopener noreferrer" 
              className="inline-block bg-ehh-orange text-ehh-cream px-10 py-4 rounded-md text-lg font-semibold hover:bg-ehh-coral transition-shadow shadow-lg"
            >
              Donate Internationally
            </a>
          </div>

          <div className="bg-white rounded-2xl shadow-xl p-12 text-center">
            <h3 className="text-2xl font-semibold text-ehh-orange mb-6">Mobile Money (Zambia)</h3>
            <p className="text-base mb-8 leading-relaxed">Convenient and instant contributions through local networks.</p>
            <ul className="text-left max-w-md mx-auto mb-10 text-base space-y-4">
              <li><strong>Airtel Money:</strong> *115# → Send Payment → Number: +260 961121278 → Amount → PIN</li>
            </ul>
            <p className="text-base">
              For more details, email{' '}
              <a href="mailto:support@emahouseofhope.org" className="text-ehh-orange hover:underline font-medium">
                support@emahouseofhope.org
              </a>
            </p>
          </div>

          <div className="bg-white rounded-2xl shadow-xl p-12 text-center">
            <h3 className="text-2xl font-semibold text-ehh-orange mb-6">Bank Transfer</h3>
            <p className="text-base mb-8 leading-relaxed">Suitable for substantial or recurring partnerships.</p>

            <div className="text-left max-w-md mx-auto space-y-8">
              <div className="bg-ehh-cream/50 rounded-xl p-6">
                <h4 className="text-lg font-semibold text-ehh-navy mb-4">For Local (Zambian) Transactions:</h4>
                <p className="text-base"><strong>Account Name:</strong> EMA House of Hope</p>
                <p className="text-base"><strong>Bank Name:</strong> Access Bank</p>
                <p className="text-base"><strong>Branch Name:</strong> Access House Branch</p>
                <p className="text-base"><strong>Account Number:</strong> 3016558578015</p>
              </div>

              <div className="bg-ehh-cream/50 rounded-xl p-6">
                <h4 className="text-lg font-semibold text-ehh-navy mb-4">For International Transactions:</h4>
                <p className="text-base"><strong>Account Name:</strong> EMA House of Hope</p>
                <p className="text-base"><strong>Bank Name:</strong> Access Bank</p>
                <p className="text-base"><strong>Account Number:</strong> 3016558578015</p>
                <p className="text-base"><strong>Swift Code:</strong> AZAMZMLU</p>
              </div>
            </div>

            <p className="text-base mt-8">
              Please forward your receipt to{' '}
              <a href="mailto:donate@emahouseofhope.org" className="text-ehh-orange hover:underline font-medium">
                donate@emahouseofhope.org
              </a>
            </p>
          </div>
        </div>
      </div>
    </section>

    {/* Closing Section */}
    <section className="py-24 px-6 text-center relative overflow-hidden bg-ehh-navy/10">
      <img 
        src="/assets/images/unity-hands.jpg" 
        alt="United hands symbolizing community partnership and hope" 
        className="absolute inset-0 w-full h-full object-cover opacity-15" 
      />
      <div className="relative container mx-auto max-w-5xl">
        <p className="text-2xl md:text-4xl font-medium mb-8 leading-relaxed">
          Together, we advance resilient communities through integrated, sustainable interventions in Zambia.
        </p>
        <p className="text-xl">Grateful for your commitment to lasting change.</p>
      </div>
    </section>
  </div>
);

export default Donate;